// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CustomQuotesDetailsPageTypeLink from "@adl/App/Components/CustomQuote/CustomQuotesDetailsPageTypeLink";
import ProductLineRemoveButton, {
    ProductLineRemoveButtonStyles,
} from "@adl/App/Components/Product/ProductLineRemoveButton";
import addQuoteTocart from "@adl/App/Handlers/CustomQuote/AddCustomQuoteToCart";
import deleteQuote from "@adl/App/Handlers/CustomQuote/DeleteCustomQuote";
import { CustomQuoteModel } from "@adl/App/Services/CustomQuotesService";
import { CustomAppState } from "@adl/App/Store/Reducers";
import { color, xScreen } from "@adl/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomAppState) => {
    return {
        quotes: state.xMyCustomQuotes.quotes,
        language: state.context.session.language,
        rfqMyQuotesPageUrl: getPageLinkByPageType(state, "RfqQuoteDetailsPage")?.url,
        isLoading: state.xMyCustomQuotes.isLoading,
    };
};

const mapDispatchToProps = {
    deleteQuote,
    addQuoteTocart,
};

type Props = HasHistory & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface MyListsListStyles {
    createdUpdatedText?: TypographyProps;
    messageText?: TypographyProps;
    noQuotesFound?: InjectableCss;
    emptyMsg?: TypographyProps;
    container?: GridContainerProps;
    rowWrapper?: GridItemProps;
    quoteRow?: GridContainerProps;
    removeButton?: ProductLineRemoveButtonStyles;
    infoGridItem?: GridItemProps;
    gridContainer?: GridContainerProps;
    infoGridContainer?: GridContainerProps;
    basicInfoGridItem?: GridItemProps;
    basicInfoGridContainer?: GridContainerProps;
    detailLinkGridItem?: GridItemProps;
    lastUpdatedGridItem?: GridItemProps;
    lastUpdatedText?: TypographyProps;
    extendedInfoGridItem?: GridItemProps;
    actionGridItem?: GridItemProps;
    actionGridMediumHidden?: HiddenProps;
    actionOverflowMenu?: OverflowMenuPresentationProps;
    actionAddToCartClickable?: ClickablePresentationProps;
    actionDeleteClickable?: ClickablePresentationProps;
    actionAddToCartButton?: ButtonPresentationProps;
    quoteInfoGridItem?: GridItemProps;
    actionDeleteButton?: ButtonPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

export const listStyles: MyListsListStyles = {
    container: {
        gap: 0,
        css: css`
            width: 100%;
            margin-top: 30px;
        `,
    },
    rowWrapper: {
        width: 12,
        css: css`
            border-bottom: 1px solid ${color("common.border")};
        `,
    },
    quoteRow: {
        gap: 5,
        css: css`
            width: 100%;
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            align-items: center;
            padding: 10px;
            position: relative;
            &:hover {
                background: ${color("common.ui1", 0.25)};
                div[data-test-selector="product_removeLine"] > div {
                    visibility: visible;
                }
            }
        `,
    },
    lastUpdatedText: {
        size: "13px",
        lineHeight: "20px",
    },
    createdUpdatedText: {
        size: "13px",
        lineHeight: "20px",
        css: css`
            font-weight: bold;
        `,
    },
    messageText: {
        variant: "h4",
        css: css`
            display: block;
            margin: auto;
        `,
    },
    noQuotesFound: {
        css: css`
            text-align: center;
            padding: 20px;
            margin-top: 80px;
        `,
    },
    emptyMsg: {
        lineHeight: "33px",
        size: "28px",
        css: css`
            font-weight: 500;
        `,
    },
    removeButton: {
        link: {
            color: "danger",
            css: css`
                margin-top: 0 !important;
            `,
        },
        innerWrapper: {
            css: css`
                position: relative;
            `,
        },
        wrapper: {
            css: css`
                margin-left: 40px;
            `,
        },
    },
    infoGridItem: {
        width: 10,
        align: "middle",
    },
    basicInfoGridItem: { width: [12, 12, 8, 8, 8] },
    basicInfoGridContainer: {
        gap: 0,
    },
    detailLinkGridItem: { width: 12 },
    lastUpdatedGridItem: {
        width: 12,
        css: css`
            display: flex;
            ${xScreen("lte", "sm")} {
                flex-direction: column;
            }
        `,
    },
    extendedInfoGridItem: {
        width: [12, 12, 4, 4, 4],
        align: "middle",
        css: css`
            ${xScreen("lte", "sm")} {
                padding-top: 0;
            }
        `,
    },
    actionGridItem: {
        width: 2,
        align: "top",
        css: css`
            justify-content: flex-end;
            align-items: center;
        `,
    },
    actionGridMediumHidden: {
        css: css`
            display: flex;
            justify-content: flex-end;
            flex-direction: column;
            align-items: center;
            width: 100%;
        `,
    },
    actionAddToCartButton: {
        css: css`
            width: 100%;
        `,
    },
    quoteInfoGridItem: {
        width: [12, 12, 8, 8, 8],
    },
    actionDeleteButton: {
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 10px;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
};

const styles = listStyles;

const MyCustomQuotesList = ({
    quotes,
    language,
    deleteQuote,
    addQuoteTocart,
    rfqMyQuotesPageUrl,
    history,
    isLoading,
}: Props) => {
    const [deleteQuoteModalIsOpen, setDeleteQuoteModalIsOpen] = useState(false);
    const [isUpdatingCart, setIsUpdatingCart] = useState(false);
    const [quote, setQuote] = useState({} as CustomQuoteModel);
    const toasterContext = useContext(ToasterContext);

    const deleteQuoteHandler = (quote: any) => {
        setQuote(quote);
        setDeleteQuoteModalIsOpen(true);
    };

    const deleteCancelHandler = () => {
        setDeleteQuoteModalIsOpen(false);
    };

    const deleteSubmitHandler = () => {
        setDeleteQuoteModalIsOpen(false);
        if (quote) {
            deleteQuote({
                quoteId: quote.id,
                onSuccess(result) {
                    onDeleteSuccess();
                },
            });
        }
    };

    const onDeleteSuccess = () => {
        toasterContext.addToast({ body: translate("Quote Deleted"), messageType: "success" });
    };

    const addQuoteToCart = (quote: CustomQuoteModel) => {
        setIsUpdatingCart(true);
        addQuoteTocart({
            quoteId: quote.id,
            onSuccess(result) {
                onAddToCartSuccess();
            },
            onError(result) {
                toasterContext.addToast({ body: result, messageType: "danger" });
            },
        });
    };

    const returnToAssociatedQuote = (quote: CustomQuoteModel) => {
        if (quote.quoteIdReference) {
            history.push(`${rfqMyQuotesPageUrl}?quoteId=${quote.quoteIdReference}`);
        }
    };

    const onAddToCartSuccess = () => {
        setIsUpdatingCart(false);
        toasterContext.addToast({ body: translate("Quote Added to Cart"), messageType: "success" });
    };

    const getModifiedOnDate = (date: string) => {
        return getLocalizedDateTime({
            dateTime: new Date(date),
            language,
            options: {
                year: "numeric",
                month: "numeric",
                day: "numeric",
            },
        });
    };

    const checkIfUpdateOn = (quote: CustomQuoteModel) => {
        if (quote.createdOn === quote.modifiedOn) {
            return false;
        }
        return true;
    };

    if (isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} data-test-selector="myLists_spinner"></LoadingSpinner>
            </StyledWrapper>
        );
    }

    if (!quotes || !quotes?.length) {
        return (
            <StyledWrapper {...styles.noQuotesFound}>
                <Typography {...styles.emptyMsg}>{translate("No Quotes Found")}</Typography>
            </StyledWrapper>
        );
    }

    return (
        <GridContainer {...styles.container}>
            {quotes.map((quote: CustomQuoteModel) => (
                <GridItem key={quote.id} {...styles.rowWrapper}>
                    <GridContainer {...styles.quoteRow} data-test-selector="customQuoteRow">
                        <GridItem {...styles.infoGridItem} data-test-selector={`${quote.id}-quote`}>
                            <GridContainer {...styles.infoGridContainer}>
                                <GridItem {...styles.basicInfoGridItem}>
                                    <GridContainer {...styles.basicInfoGridContainer}>
                                        <GridItem {...styles.detailLinkGridItem}>
                                            <CustomQuotesDetailsPageTypeLink
                                                title={quote.name}
                                                quoteId={quote.id}
                                                testSelector="wishListCardName"
                                            />
                                        </GridItem>
                                        <GridItem {...styles.lastUpdatedGridItem}>
                                            <StyledWrapper>
                                                <Typography {...styles.createdUpdatedText}>
                                                    {checkIfUpdateOn(quote)
                                                        ? translate("Updated On:")
                                                        : translate("Created On:")}
                                                    &nbsp;&nbsp;
                                                </Typography>
                                                <Typography
                                                    data-test-selector="customQuoteLastUpdated"
                                                    {...styles.lastUpdatedText}
                                                >
                                                    {getModifiedOnDate(
                                                        checkIfUpdateOn(quote) ? quote.modifiedOn : quote.createdOn,
                                                    )}
                                                    &nbsp;
                                                </Typography>
                                            </StyledWrapper>
                                            {quote.modifiedBy && (
                                                <StyledWrapper>
                                                    <Typography {...styles.createdUpdatedText}>
                                                        {translate("By")}&nbsp;
                                                    </Typography>

                                                    <Typography
                                                        data-test-selector="customQuoteUpdatedBy"
                                                        {...styles.lastUpdatedText}
                                                    >
                                                        {quote.modifiedBy}
                                                    </Typography>
                                                </StyledWrapper>
                                            )}
                                        </GridItem>
                                        {quote.expiryDate && (
                                            <GridItem {...styles.quoteInfoGridItem}>
                                                <Typography {...styles.createdUpdatedText}>
                                                    {translate("Expiration Date:")}&nbsp;
                                                </Typography>
                                                <Typography {...styles.lastUpdatedText}>{quote.expiryDate}</Typography>
                                            </GridItem>
                                        )}
                                        {quote.clientName && (
                                            <GridItem {...styles.quoteInfoGridItem}>
                                                <Typography {...styles.createdUpdatedText}>
                                                    {translate("Client Name:")}&nbsp;
                                                </Typography>
                                                <Typography {...styles.lastUpdatedText}>{quote.clientName}</Typography>
                                            </GridItem>
                                        )}
                                    </GridContainer>
                                </GridItem>
                                <GridItem {...styles.extendedInfoGridItem}>
                                    <Typography {...styles.createdUpdatedText}>
                                        {translate("Total Items:")}&nbsp;
                                    </Typography>
                                    <Typography {...styles.lastUpdatedText}>{quote.linesCount}</Typography>
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                        <GridItem {...styles.actionGridItem}>
                            <Hidden above="sm">
                                <OverflowMenu position="end" {...styles.actionOverflowMenu}>
                                    {!quote.allowRedirectToAssociatedQuote && !quote.isCameFromNormalQuote ? (
                                        <Clickable
                                            {...styles.actionAddToCartClickable}
                                            disabled={isUpdatingCart || !quote.linesCount}
                                            onClick={() => addQuoteToCart(quote)}
                                        >
                                            {translate("Add Quote to Cart")}
                                        </Clickable>
                                    ) : (
                                        <Clickable
                                            {...styles.actionAddToCartClickable}
                                            disabled={isUpdatingCart}
                                            onClick={() => returnToAssociatedQuote(quote)}
                                        >
                                            {translate("Return to Associated Quote")}
                                        </Clickable>
                                    )}
                                    <Clickable
                                        {...styles.actionDeleteClickable}
                                        onClick={() => deleteQuoteHandler(quote)}
                                    >
                                        {translate("Delete Quote")}
                                    </Clickable>
                                </OverflowMenu>
                            </Hidden>
                            <Hidden {...styles.actionGridMediumHidden} below="md">
                                {!quote.allowRedirectToAssociatedQuote && !quote.isCameFromNormalQuote ? (
                                    <Button
                                        sizeVariant="small"
                                        disabled={isUpdatingCart || !quote.linesCount}
                                        onClick={() => addQuoteToCart(quote)}
                                        {...styles.actionAddToCartButton}
                                    >
                                        {translate("Add Quote to Cart")}
                                    </Button>
                                ) : (
                                    <Button
                                        sizeVariant="small"
                                        disabled={isUpdatingCart}
                                        onClick={() => returnToAssociatedQuote(quote)}
                                        {...styles.actionAddToCartButton}
                                    >
                                        {translate("Return to Associated Quote")}
                                    </Button>
                                )}
                                <Button
                                    sizeVariant="small"
                                    disabled={isUpdatingCart}
                                    onClick={() => deleteQuoteHandler(quote)}
                                    {...styles.actionDeleteButton}
                                >
                                    {translate("Delete")}
                                </Button>
                            </Hidden>
                        </GridItem>
                    </GridContainer>
                </GridItem>
            ))}
            <TwoButtonModal
                modalIsOpen={deleteQuoteModalIsOpen}
                headlineText={translate("Delete Quote")}
                messageText={`${translate("Are you sure you want to delete")} ${quote ? quote.name : ""}?`}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                onCancel={deleteCancelHandler}
                onSubmit={deleteSubmitHandler}
                dataTestSelector="myCustomQuotes_deleteModel"
                submitTestSelector="myCustomQuotes_submitDelete"
            ></TwoButtonModal>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(MyCustomQuotesList)),
    definition: {
        group: "Custom Quote" as any,
        displayName: "Custom Quote List",
        allowedContexts: ["CustomQuotesPage" as any],
    },
};

export default widgetModule;
